import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import {
  isNoResponse,
  readStatusCode,
  retryOnServerError,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  NotificationsConsentPayload,
  NotificationsConsentResponse,
  UseShopNotificationsConsentQueryProps,
} from './types';

async function getShopNotificationsConsent(
  shopId: string,
  payload?: NotificationsConsentPayload,
): Promise<NotificationsConsentResponse> {
  try {
    const response = await apiClient.post<NotificationsConsentResponse>(
      `/v1/shop/${shopId}/notifications-consent`,
      payload,
    );
    return response.data;
  } catch (error) {
    const log = isNoResponse(error) ? Logger.warn : Logger.error;
    // Status only: the raw error's request config holds the guest's details.
    log('Error fetching shop notifications consent', {
      shopId,
      status: readStatusCode(error),
    });
    throw error;
  }
}

export const getShopNotificationConsentQuery = ({
  shopId,
  customer,
  ...options
}: UseShopNotificationsConsentQueryProps) => {
  return queryOptions({
    // The customer identity fields are part of the key so the consent prompt is
    // re-fetched (and, with the show-if-unknown fallback, re-shown) whenever the
    // customer edits any of them — mirroring water-on-web resetting consent on
    // name change. The stable prefix is ['shop', shopId, 'notifications-consent']
    // (see updateNotificationConsentMutation, which prefix-matches on it).
    queryKey: [
      'shop',
      shopId,
      'notifications-consent',
      customer.email,
      customer.phone,
      customer.firstName,
      customer.lastName,
    ],
    async queryFn() {
      if (!shopId) {
        throw new Error('Shop ID is required');
      }
      return await getShopNotificationsConsent(shopId, customer);
    },
    enabled: Boolean(shopId),
    // Fetch once per mount, but never on window focus or reconnect. The previous
    // `staleTime: -1` marked the data immediately stale, so React Query's
    // default focus refetch refired this POST on every app foreground.
    //
    // We deliberately do NOT use a long staleTime: the query client is persisted
    // to disk (IndexedDB on web, MMKV on native) via PersistQueryClientProvider,
    // so a long staleTime would rehydrate a prior session's consent as "fresh"
    // and serve stale show/hide state across reloads. staleTime: 0 means each
    // real mount/app-load re-fetches, while the disabled focus/reconnect refetch
    // prevents the within-session request storm. Mirrors water-on-web's
    // fetch-once-per-visit behaviour. (Re-fetch on name change still works via
    // the queryKey; the Yes/Skip choice still persists via the PUT.)
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryOnServerError,
    ...options,
  });
};
